import { useMemo, useState, type ReactNode } from 'react';
import { useController, type SubmitHandler, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Form,
  SelectField,
  TextareaField,
  TextField,
  useFieldError,
  useFormContext,
  type SelectOption,
} from '@/components/form';
import { CLIENT_BANK_CODES, ROMANIAN_COUNTIES } from '@/features/clients/clientFormOptions';

export type ClientFormValues = {
  firstName: string;
  lastName: string;
  company: string;
  brand: string;
  codClient: string;
  contactName: string;
  email: string;
  phone: string;
  taxId: string;
  regCom: string;
  vatPayer: boolean;
  iban: string;
  bank: string;
  billingStreet: string;
  billingCity: string;
  billingCounty: string;
  billingCountry: string;
  notes: string;
};

interface ClientFormPanelProps {
  form: UseFormReturn<ClientFormValues>;
  title: ReactNode;
  submitLabel: ReactNode;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  disabled?: boolean;
  showSubmit?: boolean;
  addressActionLabel?: ReactNode;
  addressActionDisabled?: boolean;
  addressContent?: ReactNode;
  children?: ReactNode;
  onAddressAction?: () => void;
  onCancel: () => void;
  onSubmit: SubmitHandler<ClientFormValues>;
}

const inputClass =
  'h-14 rounded-[18px] border-border bg-background px-4 text-lg shadow-none focus-visible:outline-2 focus-visible:outline-primary';
const textareaClass =
  'min-h-[150px] rounded-[18px] border-border bg-background px-4 py-3 text-lg shadow-none focus-visible:ring-primary';
const labelClass = 'mb-2 text-lg font-medium text-foreground';
const fieldGapClass = 'space-y-5';

export function ClientFormPanel({
  form,
  title,
  submitLabel,
  isSubmitting,
  submitDisabled,
  disabled,
  showSubmit = true,
  addressActionLabel,
  addressActionDisabled,
  addressContent,
  children,
  onAddressAction,
  onCancel,
  onSubmit,
}: ClientFormPanelProps) {
  const { t } = useTranslation();
  const currentBank = form.watch('bank');
  const currentCounty = form.watch('billingCounty');
  const currentCountry = form.watch('billingCountry');
  const countyOptions = ensureOption(
    [
      { value: '', label: t('clients.form.countyPlaceholder') },
      ...ROMANIAN_COUNTIES.map((county) => ({ value: county, label: county })),
    ],
    currentCounty,
  );
  const countryOptions = ensureOption(
    [{ value: 'RO', label: t('clients.countries.RO') }],
    currentCountry,
  );
  const bankOptions = ensureOption(
    [
      { value: '', label: t('clients.form.bankPlaceholder') },
      ...CLIENT_BANK_CODES.map((code) => ({
        value: code,
        label: t(`clients.banks.${code}`),
      })),
    ],
    currentBank,
  );

  return (
    <div className="w-full overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border px-7 py-6">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          className="h-10 w-10 rounded-full p-0 text-muted-foreground"
          aria-label={t('common.close')}
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      <Form form={form} onSubmit={onSubmit}>
        <div className="grid gap-8 px-7 py-8 lg:grid-cols-2">
          <div className={fieldGapClass}>
            <TextField
              name="lastName"
              label={t('clients.fields.lastName')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
              autoFocus
            />
            <TextField
              name="firstName"
              label={t('clients.fields.firstName')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <TextField
              name="taxId"
              label={t('clients.fields.taxId')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <TextField
              name="company"
              label={t('clients.fields.company')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <TextField
              name="regCom"
              label={t('clients.fields.regCom')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <TextField
              name="codClient"
              label={t('clients.fields.codClient')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <TextField
              name="brand"
              label={t('clients.fields.brand')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <VatPayerField disabled={disabled} />
            {addressActionLabel ? (
              <button
                type="button"
                onClick={onAddressAction}
                disabled={addressActionDisabled || !onAddressAction}
                className="text-left text-lg font-medium text-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                {addressActionLabel}
              </button>
            ) : null}
            {addressContent}
          </div>

          <div className={fieldGapClass}>
            <TextareaField<ClientFormValues>
              name="billingStreet"
              label={t('clients.fields.billingStreet')}
              labelClassName={labelClass}
              className={textareaClass}
              disabled={disabled}
              rows={5}
            />
            <TextField
              name="billingCity"
              label={t('clients.fields.billingCity')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <SearchableSelectField
              name="billingCounty"
              label={t('clients.fields.billingCounty')}
              options={countyOptions}
              labelClassName={labelClass}
              inputClassName={inputClass}
              placeholder={t('clients.form.countyPlaceholder')}
              disabled={disabled}
            />
            <SelectField
              name="billingCountry"
              label={t('clients.fields.billingCountry')}
              options={countryOptions}
              labelClassName={labelClass}
              triggerClassName={inputClass}
              disabled={disabled}
            />
            <TextField
              name="iban"
              label={t('clients.fields.iban')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <SelectField
              name="bank"
              label={t('clients.fields.bank')}
              options={bankOptions}
              labelClassName={labelClass}
              triggerClassName={inputClass}
              disabled={disabled}
            />
            <TextField
              name="email"
              type="email"
              label={t('clients.fields.email')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <TextField
              name="contactName"
              label={t('clients.fields.contactName')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
            <TextField
              name="phone"
              label={t('clients.fields.phone')}
              labelClassName={labelClass}
              className={inputClass}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="border-t border-border px-7 py-7">
          {children ? <div className="mb-4 space-y-2">{children}</div> : null}
          <div className="flex flex-col-reverse gap-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              className="h-14 rounded-[18px] bg-muted px-8 text-lg font-medium text-foreground hover:bg-muted/80"
            >
              {t('clients.form.cancel')}
            </Button>
            {showSubmit ? (
              <Button
                type="submit"
                disabled={submitDisabled || isSubmitting}
                className="h-14 rounded-[18px] px-8 text-lg font-medium"
                data-testid="client-submit"
              >
                {isSubmitting ? t('common.loading') : submitLabel}
              </Button>
            ) : null}
          </div>
        </div>
      </Form>
    </div>
  );
}

function VatPayerField({ disabled }: { disabled?: boolean }) {
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController<ClientFormValues>({ name: 'vatPayer' });

  return (
    <fieldset>
      <legend className={labelClass}>{t('clients.fields.vatPayer')}</legend>
      <div className="flex items-center gap-5">
        <label className="flex items-center gap-2 text-lg">
          <input
            type="radio"
            className="h-5 w-5 accent-primary"
            checked={field.value === true}
            disabled={disabled}
            onBlur={field.onBlur}
            onChange={() => field.onChange(true)}
          />
          {t('clients.vatPayerOptions.true')}
        </label>
        <label className="flex items-center gap-2 text-lg">
          <input
            type="radio"
            className="h-5 w-5 accent-primary"
            checked={field.value === false}
            disabled={disabled}
            onBlur={field.onBlur}
            onChange={() => field.onChange(false)}
          />
          {t('clients.vatPayerOptions.false')}
        </label>
      </div>
      {error && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error.message}
        </p>
      )}
    </fieldset>
  );
}

function SearchableSelectField({
  name,
  label,
  options,
  disabled,
  placeholder,
  labelClassName,
  inputClassName,
}: {
  name: string;
  label: ReactNode;
  options: SelectOption[];
  disabled?: boolean;
  placeholder?: string;
  labelClassName?: string;
  inputClassName?: string;
}) {
  const { t } = useTranslation();
  const { clearErrors, trigger } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ name });
  const errorMessage = useFieldError(name);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const fieldId = `f-${name.replace(/\W+/g, '-')}`;
  const errorId = `${fieldId}-error`;
  const listboxId = `${fieldId}-listbox`;
  const value = (field.value ?? '') as string;
  const selectedOption = options.find((option) => option.value === value);
  const filteredOptions = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return options;
    const needle = normaliseSearch(trimmed);
    return options.filter((option) => normaliseSearch(String(option.label)).includes(needle));
  }, [options, query]);
  const inputValue = open ? query : value ? String(selectedOption?.label ?? value) : '';

  const selectOption = (nextValue: string) => {
    field.onChange(nextValue);
    setQuery('');
    setOpen(false);
    if (nextValue) clearErrors(name);
    window.setTimeout(() => void trigger(name), 0);
  };

  return (
    <div className="relative">
      <Label htmlFor={fieldId} className={labelClassName}>
        {label}
      </Label>
      <Input
        id={fieldId}
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        className={inputClassName}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorMessage ? errorId : undefined}
        onFocus={() => {
          setQuery('');
          setOpen(true);
        }}
        onBlur={() => {
          field.onBlur();
          window.setTimeout(() => setOpen(false), 100);
        }}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
          if (value) field.onChange('');
          setOpen(true);
        }}
      />
      {open && !disabled && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-popover p-1 text-sm text-popover-foreground shadow-lg"
          onMouseDown={(event) => event.preventDefault()}
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-muted-foreground">
              {t('clients.form.noCountyResults')}
            </div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                className="block w-full rounded-sm px-3 py-2 text-left hover:bg-accent"
                onClick={() => selectOption(option.value)}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
      {errorMessage && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

function normaliseSearch(value: string): string {
  return value
    .toLocaleLowerCase('ro-RO')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[șş]/g, 's')
    .replace(/[țţ]/g, 't');
}

function ensureOption(options: SelectOption[], currentValue: string | undefined): SelectOption[] {
  if (!currentValue || options.some((option) => option.value === currentValue)) {
    return options;
  }
  return [...options, { value: currentValue, label: currentValue }];
}
